import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, AlertTriangle, Zap, ChevronDown, ChevronUp, X, Check } from "lucide-react";
import axios from "axios";
import Layout from "../components/custom/Layout";
import { API } from "../contexts/AuthContext";

const CLIP_8 = "polygon(8px 0%, calc(100% - 8px) 0%, 100% 8px, 100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0% calc(100% - 8px), 0% 8px)";
const CLIP_6 = "polygon(6px 0%, calc(100% - 6px) 0%, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0% calc(100% - 6px), 0% 6px)";

const M = { fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums" };
const T = { fontFamily: "'Inter Tight', sans-serif" };
const S = { fontFamily: "'IBM Plex Sans', sans-serif" };
const L = { fontFamily: "'Inter Tight', sans-serif", fontSize: "8px", letterSpacing: "0.16em", color: "#334155", fontWeight: "600" };

function probColor(v) { return v >= 70 ? "#22C55E" : v >= 55 ? "#38BDF8" : v >= 40 ? "#F59E0B" : "#EF4444"; }
function setupLabel(v) { return v >= 65 ? "Favorable" : v >= 45 ? "Neutral" : "Fragile"; }

// ─── Animated number ticker ─────────────────────────────────────
function Ticker({ value, decimals = 0, prefix = "", suffix = "", style }) {
  const [display, setDisplay] = useState(value);
  const ref = useRef(null);
  useEffect(() => {
    const start = display;
    const diff = value - start;
    if (Math.abs(diff) < 0.1) { setDisplay(value); return; }
    let frame = 0;
    const totalFrames = 30;
    const tick = () => {
      frame++;
      const progress = frame / totalFrames;
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(start + diff * eased);
      if (frame < totalFrames) ref.current = requestAnimationFrame(tick);
    };
    ref.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(ref.current);
  }, [value]); // deps: re-animate on value change
  return <span style={style}>{prefix}{display.toFixed(decimals)}{suffix}</span>;
}

// ─── Panel ────────────────────────────────────────────────────────
function P({ children, className = "", testId, glow }) {
  return (
    <div data-testid={testId} className={className}
      style={{ clipPath: CLIP_8, background: "rgba(255,255,255,0.018)", border: "1px solid rgba(255,255,255,0.04)", position: "relative", overflow: "hidden" }}>
      {glow && <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse at top left, ${glow}08, transparent 60%)` }} />}
      <div className="relative">{children}</div>
    </div>
  );
}

// ─── Mini sparkline ─────────────────────────────────────────────
function MiniSparkline({ data, width = 60, height = 22, color }) {
  if (!data || data.length < 3) return null;
  const min = Math.min(...data) - 1;
  const max = Math.max(...data) + 1;
  const range = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * (height - 2) - 1}`).join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" opacity="0.7" />
    </svg>
  );
}

// ─── Universal symbol cleaner ───────────────────────────────────
function cleanSym(s) {
  if (!s) return s;
  for (const suffix of ["USDT", "PERP", ".NS", ".BSE", ".L", "-USD"]) {
    if (s.toUpperCase().endsWith(suffix)) return s.slice(0, -suffix.length);
  }
  return s;
}

// ─── Derive quick actions with impact % ───────────────────────────
function deriveQuickActions(data) {
  const actions = [];
  const clusters = data.correlation_matrix?.clusters || [];
  const assets = data.exposure_map?.by_asset || [];
  const f = data.factor_breakdown || {};

  for (const c of clusters) {
    actions.push({
      id: `reduce-cluster-${c.name}`,
      label: `Reduce ${c.name}`,
      impact: `+${Math.min(8, Math.round((1 - (f.correlation || 5) / 10) * 12))}%`,
      type: "reduce",
    });
  }

  const dominant = assets.find(a => a.exposure_pct > 50);
  if (dominant) {
    actions.push({
      id: `trim-${dominant.symbol}`,
      label: `Trim ${dominant.display_name || cleanSym(dominant.symbol)} Exposure`,
      impact: `+${Math.min(6, Math.round((dominant.exposure_pct - 40) / 5))}%`,
      type: "trim",
    });
  }

  if (f.leverage <= 4) {
    actions.push({ id: "lower-leverage", label: "Lower Leverage", impact: `+${Math.round((10 - (f.leverage || 5)) * 0.8)}%`, type: "reduce" });
  }

  const loser = assets.find(a => a.pnl < -5000);
  if (loser) {
    actions.push({
      id: `hedge-${loser.symbol}`,
      label: `Hedge ${loser.display_name || cleanSym(loser.symbol)}`,
      impact: `+${Math.min(5, Math.round(Math.abs(loser.pnl) / 5000))}%`,
      type: "hedge",
    });
  }

  return actions.slice(0, 3);
}

// ═══════════════════════════════════════════════════════════════════
// 1. COMMAND STRIP — Dominant Ring, Compressed Labels
// ═══════════════════════════════════════════════════════════════════

// CSS keyframes injected once
const breatheCSS = `
@keyframes ringBreathe {
  0%, 100% { filter: drop-shadow(0 0 10px var(--glow-color)) brightness(1); }
  50% { filter: drop-shadow(0 0 18px var(--glow-color)) brightness(1.05); }
}
@keyframes dotPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}`;

function CommandStrip({ data }) {
  const prob = data.portfolio_probability || 0;
  const color = probColor(prob);
  const regime = data.portfolio_regime || {};
  const R = 52;
  const circumference = 2 * Math.PI * R;
  const offset = circumference - (prob / 100) * circumference;
  const history = (data.probability_history || []).map(h => h.probability).slice(-20);
  const [showSparkline, setShowSparkline] = useState(false);

  const pressureLabel = data.main_pressure_point || "";
  const clusters = data.correlation_matrix?.clusters || [];
  const pressureCtx = pressureLabel === "Correlation" && clusters.length > 0 ? `(${clusters[0].name})` : "";

  return (
    <>
      <style>{breatheCSS}</style>
      <P testId="command-strip" glow={color}>
        <div className="px-6 py-6">
          <div className="flex items-center gap-8">
            {/* ── DOMINANT RING ── */}
            <div className="flex-shrink-0 relative cursor-pointer" style={{ width: "140px", height: "140px" }}
              onMouseEnter={() => setShowSparkline(true)} onMouseLeave={() => setShowSparkline(false)}>
              <svg viewBox="0 0 120 120" width="140" height="140"
                style={{ "--glow-color": `${color}40`, animation: "ringBreathe 4s ease-in-out infinite" }}>
                <circle cx="60" cy="60" r={R} fill="none" stroke="rgba(255,255,255,0.025)" strokeWidth="5" />
                <circle cx="60" cy="60" r={R + 6} fill="none" stroke={`${color}06`} strokeWidth="1" />
                <motion.circle cx="60" cy="60" r={R} fill="none" stroke={color} strokeWidth="5.5" strokeLinecap="round"
                  strokeDasharray={circumference} initial={{ strokeDashoffset: circumference }} animate={{ strokeDashoffset: offset }}
                  transition={{ duration: 1.6, ease: "easeOut" }}
                  style={{ transform: "rotate(-90deg)", transformOrigin: "60px 60px" }} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <AnimatePresence mode="wait">
                  {!showSparkline ? (
                    <motion.div key="num" className="flex flex-col items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                      <Ticker value={prob} decimals={0} suffix="%" style={{ ...M, fontSize: "34px", fontWeight: "700", color, lineHeight: 1 }} />
                      <span style={{ ...T, fontSize: "10px", fontWeight: "700", color, marginTop: "3px", letterSpacing: "0.04em" }}>{setupLabel(prob)}</span>
                    </motion.div>
                  ) : (
                    <motion.div key="spark" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                      <MiniSparkline data={history} width={64} height={28} color={color} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* ── CENTER: compressed state + pressure + action ── */}
            <div className="flex-1 min-w-0">
              {/* Compressed single line */}
              <div className="flex items-center gap-1.5 mb-3">
                <span style={{ ...T, fontSize: "11px", fontWeight: "500", color: "#64748B" }}>
                  {data.stability_level} Stability
                </span>
                <span style={{ ...M, fontSize: "8px", color: "#1E293B" }}>&bull;</span>
                <span style={{ ...T, fontSize: "11px", fontWeight: "500", color: "#64748B" }}>
                  {data.risk_concentration} Concentration
                </span>
              </div>

              {/* Pressure */}
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={13} style={{ color: "#F59E0B", flexShrink: 0 }} />
                <span style={{ ...T, fontSize: "13px", fontWeight: "700", color: "#F59E0B" }}>
                  {pressureLabel} Pressure
                </span>
                {pressureCtx && <span style={{ ...T, fontSize: "11px", fontWeight: "500", color: "#475569" }}>{pressureCtx}</span>}
              </div>

              {/* Action */}
              <div className="flex items-start gap-2">
                <span style={{ ...T, fontSize: "12px", color: "#38BDF8", flexShrink: 0 }}>→</span>
                <span style={{ ...S, fontSize: "12px", color: "#94A3B8", lineHeight: 1.4 }}>{data.suggested_action}</span>
              </div>
            </div>

            {/* ── RIGHT: regime + equity ── */}
            <div className="flex-shrink-0 text-right">
              <div className="inline-flex items-center gap-2 px-3 py-1.5" style={{
                clipPath: CLIP_6, background: `${color}08`, border: `1px solid ${color}18`,
              }}>
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: color, boxShadow: `0 0 6px ${color}50`, animation: "dotPulse 3s ease-in-out infinite" }} />
                <span style={{ ...T, fontSize: "10px", fontWeight: "700", color }}>{regime.regime}</span>
              </div>
              <div style={{ ...M, fontSize: "9px", color: "#334155", marginTop: "6px" }}>
                ${(data.total_equity / 1000).toFixed(0)}k / {data.total_positions} pos
              </div>
            </div>
          </div>
        </div>
      </P>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 2. PORTFOLIO STRUCTURE (merged)
// ═══════════════════════════════════════════════════════════════════
function PortfolioStructure({ data }) {
  const map = data.exposure_map || {};
  const bias = map.directional_bias || {};
  const assets = (map.by_asset || []).slice(0, 4);
  const clusters = data.correlation_matrix?.clusters || [];

  return (
    <P testId="portfolio-structure">
      <div className="p-5">
        <div style={{ ...L, marginBottom: "3px" }}>STRUCTURE</div>
        <div style={{ ...T, fontSize: "12px", fontWeight: "700", color: "#E2E8F0", marginBottom: "14px" }}>Portfolio Structure</div>

        {/* Exposure bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span style={{ ...T, fontSize: "10px", fontWeight: "600", color: "#94A3B8" }}>Exposure</span>
            <span style={{ ...T, fontSize: "10px", fontWeight: "700", color: bias.net_bias === "LONG" ? "#22C55E" : "#EF4444" }}>
              {bias.net_bias} {bias.long_pct}%
            </span>
          </div>
          <div className="flex h-2 overflow-hidden" style={{ clipPath: CLIP_6 }}>
            <motion.div animate={{ width: `${bias.long_pct || 50}%` }} transition={{ duration: 1.2, ease: "easeOut" }}
              style={{ background: "linear-gradient(90deg, #22C55E70, #22C55E)" }} />
            <motion.div animate={{ width: `${bias.short_pct || 50}%` }} transition={{ duration: 1.2, ease: "easeOut" }}
              style={{ background: "linear-gradient(90deg, #EF4444, #EF444470)" }} />
          </div>
        </div>

        {/* Asset breakdown with flowing bars */}
        <div className="space-y-2 mb-4">
          {assets.map((a, i) => {
            const sym = a.display_name || cleanSym(a.symbol);
            const barColor = a.pnl >= 0 ? "#22C55E" : "#EF4444";
            const isDominant = a.exposure_pct > 50;
            return (
              <div key={i} className="flex items-center gap-2.5">
                <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: isDominant ? "#F59E0B" : "#1E293B" }} />
                <span style={{ ...T, fontSize: "10px", fontWeight: "700", color: isDominant ? "#E2E8F0" : "#64748B", width: "30px" }}>{sym}</span>
                <div className="flex-1 h-1.5 overflow-hidden" style={{ clipPath: CLIP_6, background: "rgba(255,255,255,0.03)" }}>
                  <motion.div
                    animate={{ width: `${Math.min(a.exposure_pct, 100)}%` }}
                    transition={{ duration: 1.2, ease: "easeOut", delay: i * 0.08 }}
                    style={{ height: "100%", background: `linear-gradient(90deg, ${barColor}50, ${barColor})` }} />
                </div>
                <Ticker value={a.exposure_pct} decimals={1} suffix="%" style={{ ...M, fontSize: "9px", color: "#475569", width: "32px", textAlign: "right", display: "inline-block" }} />
                <span style={{ ...M, fontSize: "9px", fontWeight: "600", color: barColor, width: "44px", textAlign: "right" }}>
                  {a.pnl >= 0 ? "+" : ""}{(a.pnl / 1000).toFixed(1)}k
                </span>
              </div>
            );
          })}
        </div>

        <div style={{ height: "1px", background: "rgba(255,255,255,0.04)", margin: "0 0 12px" }} />

        {/* Correlation insight */}
        <div>
          <span style={{ ...L, fontSize: "7px" }}>RISK</span>
          {clusters.length > 0 ? (
            <div className="mt-2 space-y-2">
              {clusters.map((c, i) => {
                const syms = (c.symbols || []);
                return (
                  <div key={i} className="flex items-center gap-2 px-3 py-2" style={{ clipPath: CLIP_6, background: "rgba(239,68,68,0.03)", border: "1px solid rgba(239,68,68,0.08)" }}>
                    <AlertTriangle size={11} style={{ color: "#EF4444", flexShrink: 0 }} />
                    <span style={{ ...T, fontSize: "10px", fontWeight: "700", color: "#EF4444" }}>{syms.join(" + ")}</span>
                    <span style={{ ...M, fontSize: "9px", color: "#64748B", marginLeft: "auto" }}>r={c.avg_correlation}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mt-2 px-3 py-2" style={{ clipPath: CLIP_6, background: "rgba(34,197,94,0.02)", border: "1px solid rgba(34,197,94,0.06)" }}>
              <span style={{ ...S, fontSize: "10px", color: "#22C55E" }}>No significant overlap</span>
            </div>
          )}
        </div>
      </div>
    </P>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 3. DECISION ENGINE — Output only: State + Confidence + Actions
// ═══════════════════════════════════════════════════════════════════
function DecisionEngine({ data }) {
  const prob = data.portfolio_probability || 0;
  const color = probColor(prob);
  const setup = setupLabel(prob);

  // Derive unique actions not already shown in command strip
  const primaryAction = prob >= 60 ? "Hold structure" : prob >= 45 ? "Monitor positions" : "Reduce exposure";
  const secondaryAction = prob >= 55 ? "Maintain current allocation" : "Tighten stop levels";

  return (
    <P testId="decision-engine" glow={color}>
      <div className="p-6">
        <div style={{ ...L, marginBottom: "3px" }}>OUTPUT</div>
        <div style={{ ...T, fontSize: "13px", fontWeight: "700", color: "#E2E8F0", marginBottom: "20px" }}>Decision Engine</div>

        {/* State + Confidence — no probability number */}
        <div className="mb-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
            <div style={{ ...T, fontSize: "28px", fontWeight: "700", color, letterSpacing: "0.02em", lineHeight: 1, filter: `drop-shadow(0 0 12px ${color}20)` }}>{setup}</div>
          </motion.div>
          <div style={{ ...T, fontSize: "10px", fontWeight: "500", color: "#475569", marginTop: "6px" }}>Confidence: {data.confidence_level}</div>
        </div>

        {/* Primary action */}
        <div className="mb-3">
          <div style={{ ...L, fontSize: "7px", marginBottom: "6px" }}>PRIMARY</div>
          <div className="flex items-center gap-2.5 px-3.5 py-3" style={{ clipPath: CLIP_6, background: "rgba(56,189,248,0.04)", border: "1px solid rgba(56,189,248,0.1)" }}>
            <span style={{ ...T, fontSize: "12px", color: "#38BDF8", flexShrink: 0 }}>→</span>
            <span style={{ ...S, fontSize: "12px", color: "#CBD5E1", fontWeight: "500" }}>{primaryAction}</span>
          </div>
        </div>

        {/* Secondary action */}
        <div>
          <div style={{ ...L, fontSize: "7px", marginBottom: "6px" }}>SECONDARY</div>
          <div className="flex items-center gap-2.5 px-3.5 py-2">
            <span style={{ ...T, fontSize: "11px", color: "#22C55E50", flexShrink: 0 }}>→</span>
            <span style={{ ...S, fontSize: "11px", color: "#64748B" }}>{secondaryAction}</span>
          </div>
        </div>
      </div>
    </P>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 4. PLATFORMS — Compact + Insight Line
// ═══════════════════════════════════════════════════════════════════
function Platforms({ data }) {
  const pls = data.platform_breakdown || [];
  const sorted = [...pls].sort((a, b) => b.equity - a.equity);
  const totalEquity = sorted.reduce((s, p) => s + p.equity, 0);
  const [showAll, setShowAll] = useState(false);
  const displayed = showAll ? sorted : sorted.slice(0, 6);

  // Find strongest platform
  const strongest = [...pls].sort((a, b) => b.pnl - a.pnl)[0];

  return (
    <P testId="platforms-compact">
      <div className="px-5 py-4">
        <div className="flex items-center justify-between mb-1">
          <div>
            <div style={L}>PLATFORMS</div>
            <span style={{ ...T, fontSize: "12px", fontWeight: "700", color: "#E2E8F0" }}>{pls.length} Connected</span>
          </div>
          {pls.length > 6 && (
            <button onClick={() => setShowAll(!showAll)} data-testid="platforms-toggle"
              className="flex items-center gap-1 transition-colors hover:text-white"
              style={{ ...T, fontSize: "9px", fontWeight: "600", color: "#475569", letterSpacing: "0.06em" }}>
              {showAll ? "LESS" : `ALL ${pls.length}`} {showAll ? <ChevronUp size={9} /> : <ChevronDown size={9} />}
            </button>
          )}
        </div>

        {/* Insight line */}
        {strongest && (
          <div className="mb-3" data-testid="platform-insight">
            <span style={{ ...S, fontSize: "10px", color: "#64748B" }}>
              Strongest: <span style={{ color: "#E2E8F0", fontWeight: "600" }}>{strongest.platform}</span>
              <span style={{ color: strongest.pnl >= 0 ? "#22C55E" : "#EF4444", fontWeight: "600", marginLeft: "4px" }}>
                ({strongest.pnl >= 0 ? "+" : ""}${strongest.pnl.toFixed(0)})
              </span>
            </span>
          </div>
        )}

        <div className="space-y-0.5">
          {displayed.map((p, i) => {
            const pct = totalEquity > 0 ? (p.equity / totalEquity * 100) : 0;
            const rc = p.risk_level === "HIGH" ? "#EF4444" : p.risk_level === "MODERATE" ? "#F59E0B" : "#22C55E";
            return (
              <div key={i} data-testid={`plat-${i}`} className="flex items-center gap-2 px-2 py-1.5 transition-colors hover:bg-white/[0.01]">
                <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: rc }} />
                <span style={{ ...T, fontSize: "9px", fontWeight: "600", color: "#94A3B8", width: "80px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.platform}</span>
                <div className="flex-1 h-1 overflow-hidden" style={{ clipPath: CLIP_6, background: "rgba(255,255,255,0.025)" }}>
                  <motion.div animate={{ width: `${pct}%` }} transition={{ duration: 1, ease: "easeOut", delay: i * 0.03 }}
                    style={{ height: "100%", background: "#2563EB50" }} />
                </div>
                <span style={{ ...M, fontSize: "9px", fontWeight: "600", color: p.pnl >= 0 ? "#22C55E" : "#EF4444", width: "40px", textAlign: "right" }}>
                  {p.pnl >= 0 ? "+" : ""}{p.pnl.toFixed(0)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </P>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 5. EXECUTE LAYER — Quick Actions with impact + micro-confirmation
// ═══════════════════════════════════════════════════════════════════
function ExecuteLayer({ data }) {
  const actions = deriveQuickActions(data);
  const [dismissed, setDismissed] = useState([]);
  const [confirmed, setConfirmed] = useState(null);
  const [visible, setVisible] = useState(true);

  const active = actions.filter(a => !dismissed.includes(a.id));
  if (active.length === 0 || !visible) return null;

  const typeColors = {
    reduce: { color: "#F59E0B", bg: "rgba(245,158,11,0.03)", border: "rgba(245,158,11,0.1)", hoverBg: "rgba(245,158,11,0.06)" },
    trim: { color: "#38BDF8", bg: "rgba(56,189,248,0.03)", border: "rgba(56,189,248,0.1)", hoverBg: "rgba(56,189,248,0.06)" },
    hedge: { color: "#A855F7", bg: "rgba(168,85,247,0.03)", border: "rgba(168,85,247,0.1)", hoverBg: "rgba(168,85,247,0.06)" },
  };

  const handleClick = (id) => {
    setConfirmed(id);
    setTimeout(() => { setDismissed(d => [...d, id]); setConfirmed(null); }, 800);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.5 }}
      data-testid="execute-layer"
      className="fixed bottom-6 right-8 z-50"
      style={{ width: "340px" }}
    >
      <div style={{
        clipPath: CLIP_8, background: "rgba(8,10,14,0.96)", backdropFilter: "blur(24px)",
        border: "1px solid rgba(255,255,255,0.06)", boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
      }}>
        {/* Header */}
        <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
          <div className="flex items-center gap-2">
            <Zap size={11} style={{ color: "#F59E0B" }} />
            <span style={{ ...T, fontSize: "10px", fontWeight: "700", color: "#E2E8F0", letterSpacing: "0.1em" }}>EXECUTE LAYER</span>
          </div>
          <div className="flex items-center gap-3">
            <span style={{ ...T, fontSize: "8px", fontWeight: "500", color: "#334155", letterSpacing: "0.06em" }}>RECOMMENDED MOVES</span>
            <button onClick={() => setVisible(false)} className="text-[#334155] hover:text-white transition-colors" data-testid="dismiss-execute-layer">
              <X size={12} />
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="px-4 py-3 space-y-2.5">
          {active.map((a) => {
            const tc = typeColors[a.type] || typeColors.reduce;
            const isConfirmed = confirmed === a.id;
            return (
              <motion.div key={a.id} data-testid={`exec-${a.id}`}
                className="relative cursor-pointer group"
                onClick={() => handleClick(a.id)}
                whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}>
                <div className="flex items-center gap-3 px-3.5 py-3 transition-all duration-200"
                  style={{
                    clipPath: CLIP_6,
                    background: isConfirmed ? `${tc.color}10` : tc.bg,
                    border: `1px solid ${isConfirmed ? tc.color + "30" : tc.border}`,
                    boxShadow: isConfirmed ? `0 0 12px ${tc.color}15` : "none",
                  }}>
                  {/* Icon */}
                  <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-sm transition-all"
                    style={{ background: `${tc.color}10`, boxShadow: `0 0 0 0 ${tc.color}00` }}>
                    {isConfirmed ? <Check size={11} style={{ color: "#22C55E" }} /> : <Zap size={11} style={{ color: tc.color }} />}
                  </div>

                  {/* Label only — no description */}
                  <div className="flex-1 min-w-0">
                    <div style={{ ...T, fontSize: "10px", fontWeight: "700", color: tc.color }}>{a.label}</div>
                  </div>

                  {/* Impact badge */}
                  <div className="flex-shrink-0 px-2 py-0.5" style={{ clipPath: CLIP_6, background: `${tc.color}0A`, border: `1px solid ${tc.color}15` }}>
                    <span style={{ ...M, fontSize: "9px", fontWeight: "700", color: tc.color }}>{a.impact}</span>
                  </div>
                </div>

                {/* Hover glow overlay */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{ clipPath: CLIP_6, background: `radial-gradient(ellipse at center, ${tc.color}04, transparent 70%)` }} />
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════
export default function IntelligenceGrid() {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchIntelligence = useCallback(async () => {
    try {
      const [intRes, sumRes] = await Promise.all([
        axios.get(`${API}/intelligence/portfolio`),
        axios.get(`${API}/dashboard/summary`),
      ]);
      setData(intRes.data);
      setSummary(sumRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchIntelligence(); const iv = setInterval(fetchIntelligence, 10000); return () => clearInterval(iv); }, [fetchIntelligence]);

  return (
    <Layout summary={summary}>
      <div className="p-6 h-full overflow-auto" data-testid="intelligence-grid">
        <div className="mb-4">
          <div style={L}>INTELLIGENCE</div>
          <h1 style={{ ...T, fontWeight: "700", color: "#E2E8F0", fontSize: "15px", letterSpacing: "0.06em", marginTop: "2px" }}>PORTFOLIO INTELLIGENCE</h1>
          <div style={{ height: "1px", background: "rgba(255,255,255,0.04)", marginTop: "8px" }} />
        </div>

        {loading && (
          <div className="space-y-4">
            <div className="animate-pulse" style={{ height: "140px", clipPath: CLIP_8, background: "rgba(255,255,255,0.02)" }} />
            <div className="grid grid-cols-2 gap-4">
              <div className="animate-pulse" style={{ height: "220px", clipPath: CLIP_8, background: "rgba(255,255,255,0.015)" }} />
              <div className="animate-pulse" style={{ height: "220px", clipPath: CLIP_8, background: "rgba(255,255,255,0.015)" }} />
            </div>
          </div>
        )}

        {data && (
          <div className="space-y-4">
            <CommandStrip data={data} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <PortfolioStructure data={data} />
              <DecisionEngine data={data} />
            </div>
            <Platforms data={data} />
            <div className="flex items-center gap-2 pt-1" style={{ borderTop: "1px solid rgba(255,255,255,0.03)" }}>
              <Activity size={8} style={{ color: "#334155" }} />
              <span style={{ ...M, fontSize: "8px", color: "#334155" }}>
                {data.last_updated ? new Date(data.last_updated).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"}
              </span>
            </div>
            <ExecuteLayer data={data} />
          </div>
        )}
      </div>
    </Layout>
  );
}
